import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { useCompany } from '@/context/CompanyContext';
import { supabase } from '@/lib/supabase';
import { Card, CardHeader, CardBody, Button } from '@/components/ui';
import { SettingsTabs } from '@/components/settings';
import { 
  Building2, 
  Globe, 
  FileText, 
  Hash, 
  MapPin, 
  AlertCircle, 
  CheckCircle2,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import type { Company } from '@/types/database';

export function CompanySettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Pull the actively-selected company straight from the global context.
  // When the topbar company-selector changes, activeCompany / activeCompanyId
  // updates here automatically, triggering the useEffect below to re-fetch.
  const { activeCompanyId, loadingCompanies } = useCompany();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);
  const [legalName, setLegalName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Re-fetch full company row whenever the active company changes.
  // We use activeCompanyId (not activeCompany directly) as the dependency so
  // any company-switch in the topbar triggers a fresh DB read.
  useEffect(() => {
    const fetchCompany = async () => {
      // Still loading the companies list – wait for it.
      if (loadingCompanies) return;

      if (!user?.id || !activeCompanyId) {
        setLoading(false);
        navigate('/workspaces');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch the full company row by its ID so we always reflect the
        // currently selected workspace.
        const { data, error: fetchError } = await supabase
          .from('companies')
          .select('*')
          .eq('id', activeCompanyId)
          .single();

        if (fetchError || !data) {
          // No access or not found – fall back to workspaces picker.
          navigate('/workspaces');
          return;
        }

        setCompany(data as Company);
        setLegalName(data.legal_name);
      } catch (err) {
        console.error('Error fetching company:', err);
        setError('Failed to load company data');
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompanyId, loadingCompanies]);

  const handleSave = async () => {
    if (!company?.id || !legalName.trim()) {
      setError('Legal name is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: updateError } = await supabase
        .from('companies')
        .update({ legal_name: legalName.trim() })
        .eq('id', company.id);

      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }

      // Update local state
      setCompany({ ...company, legal_name: legalName.trim() });
      setSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating company:', err);
      setError('Failed to update company. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Loading states ──────────────────────────────────────────────────────────

  // While the global context is still fetching companies, show spinner.
  if (loadingCompanies || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading company settings...</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Company not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button & Header */}
      <div>
        <button
          onClick={() => navigate('/workspaces')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Workspaces</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-1">Company Settings</h1>
        <p className="text-gray-600 mb-6">
          Manage your company information and team members
        </p>
      </div>

      {/* Settings Tabs Navigation */}
      <SettingsTabs />

      {/* Success Alert */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3 animate-fade-in">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">
              Changes Saved
            </p>
            <p className="text-sm text-green-700 mt-1">
              Your company information has been updated successfully.
            </p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 animate-fade-in">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Company Settings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Company Information
            </h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Edit your company's legal name. Other fields are read-only for security.
          </p>
        </CardHeader>

        <CardBody>
          <div className="space-y-6">
            {/* Legal Name - EDITABLE */}
            <div>
              <label
                htmlFor="legal_name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Legal Company Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="legal_name"
                  type="text"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  placeholder="Acme Corporation Inc."
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                This is the only field you can edit
              </p>
            </div>

            {/* Country - READ ONLY */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Country of Incorporation
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={company.country}
                  disabled
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Read-only</p>
            </div>

            {/* Jurisdiction - READ ONLY (if exists) */}
            {company.jurisdiction && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Jurisdiction (State/Province)
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={company.jurisdiction}
                    disabled
                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Read-only</p>
              </div>
            )}

            {/* Registration Number - READ ONLY */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Registration Number
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={company.registration_number}
                  disabled
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Read-only for security</p>
            </div>

            {/* Tax ID - READ ONLY */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tax ID
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={company.tax_id}
                  disabled
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Read-only for security</p>
            </div>

            {/* Local Requirements - READ ONLY (if exists) */}
            {company.local_requirements && Object.keys(company.local_requirements).length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">
                  Country-Specific Requirements
                </h3>
                <div className="space-y-4">
                  {Object.entries(company.local_requirements).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 capitalize">
                        {key.replace(/_/g, ' ')}
                      </label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={value}
                          disabled
                          className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Read-only</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* LEI - READ ONLY (if exists) */}
            {company.lei && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  LEI (Legal Entity Identifier)
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={company.lei}
                    disabled
                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Read-only</p>
              </div>
            )}

            {/* DUNS - READ ONLY (if exists) */}
            {company.duns && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  DUNS Number
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={company.duns}
                    disabled
                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Read-only</p>
              </div>
            )}

            {/* Save Button */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleSave}
                  isLoading={saving}
                  disabled={saving || legalName.trim() === company.legal_name}
                  className="px-6 py-3"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  onClick={() => navigate('/workspaces')}
                  variant="outline"
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
